/**
 * 认证 API 服务
 *
 * 提供登录、登出、获取当前用户等认证相关 API 调用
 */

import { request, setAuthToken, clearAuthToken, getAuthToken } from './request'
import type { TokenResponse, User, UserUpdate } from '@/types/auth'

/** API 基础 URL */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

/**
 * 认证 API
 */
export const authApi = {
  /**
   * 用户登录
   *
   * @param username - 用户名
   * @param password - 密码
   * @returns TokenResponse - 包含 access_token 的响应
   */
  async login(username: string, password: string): Promise<TokenResponse> {
    // 登录使用 form-urlencoded 格式
    const formData = new URLSearchParams()
    formData.append('username', username)
    formData.append('password', password)

    const response = await fetch(`${BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const detail = (errorData as { detail?: string }).detail || '登录失败'
      throw new Error(detail)
    }

    const data: TokenResponse = await response.json()

    // 保存 token
    setAuthToken(data.access_token)

    return data
  },

  /**
   * 用户登出
   */
  async logout(): Promise<void> {
    const token = getAuthToken()
    if (token) {
      try {
        await request('/api/auth/logout', {
          method: 'POST',
          requireAuth: true,
        })
      } catch {
        // 忽略登出错误，继续清除本地状态
      }
    }
    clearAuthToken()
  },

  /**
   * 获取当前用户信息
   */
  async getCurrentUser(): Promise<User> {
    return request<User>('/api/auth/users/me', {
      method: 'GET',
      requireAuth: true,
    })
  },

  /**
   * 更新当前用户信息
   */
  async updateCurrentUser(data: UserUpdate): Promise<User> {
    return request<User>('/api/auth/users/me', {
      method: 'PATCH',
      data,
      requireAuth: true,
    })
  },
}
